import React, { useState } from 'react';
import { ArrowLeft, Bot, CheckCircle2, ChevronDown, ChevronUp, ClipboardList, Copy, Check, BookmarkX, EyeOff, Shield, Sparkles, Cloud, Server, Database, UserCircle, Calendar, Flag, Send, X, Crown, Users, Eye } from 'lucide-react';
import type { Environment } from '../components/RecentScans';
// ... existing code ...
interface TeamMember {
  id: string;
  name: string;
  email: string;
  initials: string;
  role: 'Admin' | 'Analyst' | 'Responder' | 'Viewer';
}
interface TaskAssignment {
  findingId: string;
  assignee: TeamMember | null;
  priority: 'Critical' | 'High' | 'Medium' | 'Low';
  dueDate: string;
  notes: string;
}
const teamMembers: TeamMember[] = [{
  id: 'm1',
  name: 'Sarah Chen',
  email: 'sarah.chen@astrolayb.com',
  initials: 'SC',
  role: 'Admin'
}, {
  id: 'm2',
  name: 'James Wilson',
  email: 'j.wilson@astrolayb.com',
  initials: 'JW',
  role: 'Analyst'
}, {
  id: 'm3',
  name: 'Priya Patel',
  email: 'p.patel@astrolayb.com',
  initials: 'PP',
  role: 'Analyst'
}, {
  id: 'm4',
  name: 'Alex Rivera',
  email: 'a.rivera@astrolayb.com',
  initials: 'AR',
  role: 'Responder'
}, {
  id: 'm5',
  name: 'Maya Kim',
  email: 'm.kim@astrolayb.com',
  initials: 'MK',
  role: 'Responder'
}, {
  id: 'm6',
  name: 'David Lee',
  email: 'd.lee@astrolayb.com',
  initials: 'DL',
  role: 'Viewer'
}];
const roleIcons: Record<string, React.ReactNode> = {
  Admin: <Crown size={10} data-id="element-1141" />,
  Analyst: <Shield size={10} data-id="element-1142" />,
  Responder: <Users size={10} data-id="element-1143" />,
  Viewer: <Eye size={10} data-id="element-1144" />
};
const roleColors: Record<string, string> = {
  Admin: 'text-astro-green',
  Analyst: 'text-white',
  Responder: 'text-astro-text-secondary',
  Viewer: 'text-astro-text-secondary/70'
};
export function ClientVulnerabilityPage({
  environment,
  onBack
}: ClientVulnerabilityPageProps) {
  const [expandedFinding, setExpandedFinding] = useState<string | null>(null);
  const [copiedResource, setCopiedResource] = useState<string | null>(null);
  const [dismissedFindings, setDismissedFindings] = useState<string[]>([]);
  const [taskCreation, setTaskCreation] = useState<TaskAssignment | null>(null);
  const [showAssigneeDropdown, setShowAssigneeDropdown] = useState(false);
  const [createdTasks, setCreatedTasks] = useState<Record<string, {
    assignee: TeamMember;
    priority: string;
  }>>({});
  const handleCopyResource = (resource: string, id: string) => {
    navigator.clipboard?.writeText(resource);
    setCopiedResource(id);
    setTimeout(() => setCopiedResource(null), 2000);
  };
  const startTaskCreation = (findingId: string, severity: string) => {
    setTaskCreation({
      findingId,
      assignee: null,
      priority: severity === 'HIGH' ? 'Critical' : severity === 'MEDIUM' ? 'High' : 'Medium',
      dueDate: '',
      notes: ''
    });
    setShowAssigneeDropdown(false);
  };
  const handleCreateTask = () => {
    if (!taskCreation || !taskCreation.assignee) return;
    setCreatedTasks(prev => ({
      ...prev,
      [taskCreation.findingId]: {
        assignee: taskCreation.assignee!,
        priority: taskCreation.priority
      }
    }));
    const findingId = taskCreation.findingId;
    setTaskCreation(null);
    setShowAssigneeDropdown(false);
    // Dismiss after a brief moment to show the success state
    setTimeout(() => {
      setDismissedFindings(prev => [...prev, findingId]);
      setCreatedTasks(prev => {
        const next = {
          ...prev
        };
        delete next[findingId];
        return next;
      });
    }, 2000);
  };
  const cancelTaskCreation = () => {
    setTaskCreation(null);
    setShowAssigneeDropdown(false);
  };
  const getFindingsForClient = (env: Environment): RemediationFinding[] => {
    switch (env.clientName) {
      case 'Acme Corp':
        return [{
          id: 'REM-A01',
          severity: 'LOW',
          title: 'Unused service accounts older than 90 days',
          resourceType: 'Service Account',
          resourceName: 'svc-acme-legacy',
          affected: 4,
          description: 'Service accounts have not authenticated in over 90 days. These dormant identities increase the attack surface and should be removed if no longer required.',
          affectedResource: `sa:acme-corp:legacy-service-account`,
          recommendation: 'Disable the service accounts. If no issues arise after 30 days, permanently delete them.'
        }, {
          id: 'REM-A02',
          severity: 'LOW',
          title: 'Password policy below recommended strength',
          resourceType: 'IAM Policy',
          resourceName: 'default-password-policy',
          affected: 2,
          description: 'The current password policy allows passwords shorter than 12 characters and does not enforce complexity requirements.',
          affectedResource: `policy:acme-corp:password-policy`,
          recommendation: 'Update the password policy to require a minimum of 14 characters, mixed case, numbers, and special characters.'
        }];
      case 'Globex Inc':
        return [{
          id: 'REM-G01',
          severity: 'MEDIUM',
          title: 'Overly Broad S3 Bucket Policy',
          resourceType: 'S3 Bucket Policy',
          resourceName: 'globex-data-lake',
          affected: 5,
          description: 'S3 bucket policy grants s3:* to a wide set of IAM principals. Multiple service roles have full read/write access when only read is required for most.',
          affectedResource: `arn:aws:s3:::globex-data-lake`,
          recommendation: 'Restrict bucket policy to specific actions (s3:GetObject, s3:ListBucket) per principal. Use separate policies for write access where needed.',
          suggestedPolicy: `{
  "effect": "Allow",
  "principal": { "AWS": "arn:aws:iam::role/data-reader" },
  "actions": ["s3:GetObject", "s3:ListBucket"],
  "resources": ["arn:aws:s3:::globex-data-lake/*"]
}`
        }, {
          id: 'REM-G02',
          severity: 'MEDIUM',
          title: 'Cross-Account Role Trust Too Permissive',
          resourceType: 'IAM Role',
          resourceName: 'role-globex-cross-acct',
          affected: 3,
          description: 'Cross-account assume-role trust policy allows any principal from the trusted account to assume this role. Should be scoped to specific roles or users.',
          affectedResource: `arn:iam::${env.id}:role/cross-account-admin`,
          recommendation: 'Update trust policy to specify exact principal ARNs instead of the root account. Add external ID condition for additional security.'
        }, {
          id: 'REM-G03',
          severity: 'LOW',
          title: 'CloudWatch Log Group Retention Not Set',
          resourceType: 'CloudWatch',
          resourceName: '/aws/lambda/globex-api',
          affected: 8,
          description: 'CloudWatch log groups have no retention policy set, defaulting to indefinite retention. This increases storage costs.',
          affectedResource: `arn:aws:logs:us-east-1:${env.id}:log-group:/aws/lambda/api`,
          recommendation: 'Set retention policy to 90 days for operational logs and 365 days for audit logs.'
        }, {
          id: 'REM-G04',
          severity: 'HIGH',
          title: 'Shadow AI Copilot with Tenant-Wide OAuth Consent',
          resourceType: 'OAuth App Registration',
          resourceName: 'app-globex-ai-assistant-unregistered',
          affected: 6,
          description: 'An unauthorized third-party AI copilot application was discovered with admin-consented OAuth permissions across the Globex tenant. The app holds Mail.ReadWrite, Files.ReadWrite.All, Sites.ReadWrite.All, and Chat.ReadWrite scopes. It was registered by a non-admin user and granted tenant-wide consent without going through the app governance review process. The application is actively reading mailbox and SharePoint data from 6 user accounts.',
          affectedResource: `azure:app-registration:globex-ai-assistant:client-id-4f8a...c2e1`,
          recommendation: 'Immediately revoke the OAuth consent grant and disable the application registration. Audit all data accessed by the app in the last 90 days via Azure AD sign-in and audit logs. Implement an admin consent workflow to prevent future unauthorized app registrations.',
          suggestedPolicy: `{
  "adminConsentWorkflow": {
    "isEnabled": true,
    "notifyReviewers": true,
    "reviewers": ["security-team@globex.com"],
    "remindersEnabled": true,
    "requestExpiresInDays": 7
  },
  "appConsentPolicy": {
    "permissionGrantCondition": "admin-only",
    "blockUserConsent": true,
    "allowedPermissions": ["User.Read", "openid", "profile"]
  }
}`
        }];
      case 'Soylent Corp':
        return [{
          id: 'REM-S01',
          severity: 'HIGH',
          title: 'Service Account with Owner Role',
          resourceType: 'Service Principal',
          resourceName: 'sp-soylent-corp-deploy',
          affected: 3,
          description: 'Service principal has Owner role assigned at subscription scope. This grants full control over all resources including the ability to assign roles to other identities. This violates least-privilege principles.',
          affectedResource: `arn:iam::${env.id}:role/sp-deploy-owner`,
          recommendation: 'Scope down to Contributor on specific resource groups. Remove role assignment capability by switching from Owner to a custom role.',
          suggestedPolicy: `{
  "roleName": "Custom-Deploy-Contributor",
  "scope": "/subscriptions/.../resourceGroups/prod",
  "permissions": {
    "actions": ["Microsoft.Resources/*", "Microsoft.Compute/*"],
    "notActions": ["*/roleAssignments/*"]
  }
}`
        }, {
          id: 'REM-S02',
          severity: 'HIGH',
          title: 'Stale Admin Account — No MFA',
          resourceType: 'User Account',
          resourceName: 'admin-legacy@soylent.com',
          affected: 1,
          description: 'Admin account has not authenticated in 90+ days but retains Global Administrator role. MFA is not enforced via Conditional Access policy. High risk for credential compromise.',
          affectedResource: `user:admin-legacy@soylent.com`,
          recommendation: 'Disable account immediately. If still needed, enforce MFA via Conditional Access and downgrade to a scoped admin role.'
        }, {
          id: 'REM-S03',
          severity: 'MEDIUM',
          title: 'Wildcard Permissions on IAM Policy',
          resourceType: 'IAM Policy',
          resourceName: 'policy-soylent-legacy',
          affected: 12,
          description: 'IAM policy uses wildcard (*) actions on multiple resource types. This grants broad access that likely exceeds operational requirements.',
          affectedResource: `arn:iam::${env.id}:policy/legacy-admin-access`,
          recommendation: 'Analyze CloudTrail/Activity logs to identify actually-used permissions. Generate a least-privilege policy based on 90-day usage data.',
          suggestedPolicy: `{
  "effect": "Allow",
  "actions": [
    "s3:GetObject", "s3:PutObject",
    "ec2:DescribeInstances", "ec2:StartInstances",
    "logs:CreateLogGroup", "logs:PutLogEvents"
  ],
  "resources": ["arn:aws:s3:::prod-bucket/*"]
}`
        }, {
          id: 'REM-S04',
          severity: 'MEDIUM',
          title: 'Unused API Keys — Active 180+ Days',
          resourceType: 'Access Key',
          resourceName: 'AKIA...X7Q9 (Soylent)',
          affected: 2,
          description: 'Access keys have been active for 180+ days with no recent API calls. Unused credentials increase the attack surface and risk of credential leakage.',
          affectedResource: `arn:iam::${env.id}:user/svc-pipeline/accesskey/AKIA...X7Q9`,
          recommendation: 'Deactivate unused keys immediately. Implement automated key rotation policy with a 90-day maximum lifetime.'
        }, {
          id: 'REM-S05',
          severity: 'HIGH',
          title: 'Unregistered AI Agent with Admin API Access',
          resourceType: 'Service Account (Shadow AI)',
          resourceName: 'svc-soylent-llm-agent-prod',
          affected: 8,
          description: 'An autonomous AI agent was discovered operating in the production environment with admin-level API credentials. The service account "svc-soylent-llm-agent-prod" was provisioned 62 days ago outside the standard change management process and is not tracked in the service catalog. The agent has made 14,200+ API calls including write operations to production databases, secret store reads, and IAM role modifications. Analysis indicates it is an LLM-based automation agent (likely LangChain or similar framework) performing infrastructure management tasks without human approval gates.',
          affectedResource: `arn:iam::${env.id}:service-account/svc-soylent-llm-agent-prod`,
          recommendation: 'Immediately rotate the API credentials and restrict the service account to read-only access. Conduct a full audit of all actions performed by this identity over the last 62 days. Register the agent in the service catalog with a designated owner. Implement mandatory human-in-the-loop approval for any write operations, and enforce a dedicated "AI Agent" IAM policy with scoped permissions.',
          suggestedPolicy: `{
  "policyName": "AI-Agent-Restricted-ReadOnly",
  "description": "Scoped policy for registered AI agents",
  "permissions": {
    "actions": [
      "s3:GetObject", "s3:ListBucket",
      "ec2:Describe*", "rds:Describe*",
      "logs:GetLogEvents", "logs:FilterLogEvents",
      "cloudwatch:GetMetricData"
    ],
    "notActions": [
      "iam:*", "s3:PutObject", "s3:DeleteObject",
      "ec2:RunInstances", "ec2:TerminateInstances",
      "rds:ModifyDBInstance", "rds:DeleteDBInstance",
      "secretsmanager:GetSecretValue"
    ]
  },
  "conditions": {
    "requireApprovalGate": true,
    "maxSessionDuration": "1h",
    "mandatoryTags": ["ai-agent", "owner", "purpose"]
  }
}`
        }];
      case 'Umbrella Corp':
        return [{
          id: 'REM-U01',
          severity: 'LOW',
          title: 'Conditional Access Policy Gap for Guest Users',
          resourceType: 'Conditional Access',
          resourceName: 'CA-Policy-Guests',
          affected: 3,
          description: 'Guest users are not covered by the standard MFA enforcement policy. This leaves external access vectors unprotected.',
          affectedResource: `azure:policy:ca-guest-access`,
          recommendation: 'Create a specific Conditional Access policy for all guest users requiring MFA and accepting terms of use.'
        }, {
          id: 'REM-U02',
          severity: 'LOW',
          title: 'Service Principal Certificate Expiring in 30 Days',
          resourceType: 'Service Principal',
          resourceName: 'sp-umbrella-reporting',
          affected: 1,
          description: 'The certificate used for authentication by the reporting service principal will expire in less than 30 days.',
          affectedResource: `azure:sp:umbrella-reporting-cert`,
          recommendation: 'Rotate the certificate immediately and update the application configuration.'
        }];
      case 'Stark Ind':
        return [{
          id: 'REM-ST01',
          severity: 'MEDIUM',
          title: 'Overly Permissive IAM Binding on Project',
          resourceType: 'IAM Binding',
          resourceName: 'project-stark-weapons-dev',
          affected: 6,
          description: 'Project-level IAM binding grants Editor role to a Google Group that includes external users.',
          affectedResource: `gcp:project:stark-weapons-dev:binding`,
          recommendation: 'Remove the broad Editor role. Assign specific roles (e.g., Cloud Build Editor, Storage Object Admin) to the group.',
          suggestedPolicy: `{
  "bindings": [
    {
      "role": "roles/storage.objectAdmin",
      "members": ["group:external-devs@stark.com"]
    },
    {
      "role": "roles/cloudbuild.builds.editor",
      "members": ["group:external-devs@stark.com"]
    }
  ]
}`
        }, {
          id: 'REM-ST02',
          severity: 'MEDIUM',
          title: 'Service Account Key Not Rotated in 180 Days',
          resourceType: 'Service Account Key',
          resourceName: 'sa-jarvis-backend',
          affected: 4,
          description: 'User-managed service account keys have not been rotated in over 180 days.',
          affectedResource: `gcp:sa:jarvis-backend:key`,
          recommendation: 'Rotate keys immediately. Consider using Workload Identity Federation to eliminate the need for long-lived keys.'
        }, {
          id: 'REM-ST03',
          severity: 'LOW',
          title: 'Audit Log Sink Missing for Critical Projects',
          resourceType: 'Cloud Logging',
          resourceName: 'sink-stark-audit',
          affected: 2,
          description: 'Critical projects do not have a configured log sink to export audit logs to a centralized storage bucket for long-term retention.',
          affectedResource: `gcp:logging:sink:central-audit`,
          recommendation: 'Configure a log sink to export all Admin Activity and Data Access logs to the central audit bucket.'
        }, {
          id: 'REM-ST04',
          severity: 'MEDIUM',
          title: 'Agentic AI Workflow with Autonomous Resource Provisioning',
          resourceType: 'Service Account (Shadow AI)',
          resourceName: 'sa-jarvis-agent-orchestrator',
          affected: 14,
          description: 'A GCP service account linked to an AI orchestration platform (identified as a JARVIS-based agentic workflow) has been autonomously provisioning cloud resources without human approval. In the last 30 days, the agent has created 14 Compute Engine instances, 3 Cloud Functions, and 2 Cloud SQL instances — none of which appear in Terraform state or change management records. The service account holds Project Editor and Cloud SQL Admin roles.',
          affectedResource: `gcp:sa:jarvis-agent-orchestrator@stark-weapons-dev.iam.gserviceaccount.com`,
          recommendation: 'Restrict the service account to read-only roles immediately. Audit all resources provisioned by this identity and reconcile with infrastructure-as-code state. Implement Organization Policy constraints to require labels on all new resources and block service accounts from creating resources without an associated Terraform run ID.',
          suggestedPolicy: `{
  "orgPolicy": {
    "constraint": "constraints/compute.requireLabels",
    "enforced": true,
    "requiredLabels": ["managed-by", "owner", "change-ticket"]
  },
  "iamUpdate": {
    "remove": ["roles/editor", "roles/cloudsql.admin"],
    "add": [
      "roles/compute.viewer",
      "roles/cloudsql.viewer",
      "roles/monitoring.viewer"
    ],
    "condition": {
      "title": "AI Agent Read-Only",
      "expression": "request.auth.claims.agent_type == 'orchestrator'"
    }
  }
}`
        }];
      default:
        return [];
    }
  };
  const findings = getFindingsForClient(environment);
  const activeFindings = findings.filter(f => !dismissedFindings.includes(f.id));
  const highCount = activeFindings.filter(f => f.severity === 'HIGH').length;
  const mediumCount = activeFindings.filter(f => f.severity === 'MEDIUM').length;
  const lowCount = activeFindings.filter(f => f.severity === 'LOW').length;
  const getTypeIcon = (type: Environment['type']) => {
    switch (type) {
      case 'AWS':
      case 'Azure':
      case 'GCP':
        return <Cloud size={14} data-id="element-1145" />;
      case 'On-Prem':
        return <Server size={14} data-id="element-1146" />;
      case 'Hybrid':
        return <Database size={14} data-id="element-1147" />;
      default:
        return <Server size={14} data-id="element-1148" />;
    }
  };
  const getCmmcColor = (level: number) => {
    if (level >= 3) return 'text-astro-green bg-astro-green/10 border-astro-green/30';
    if (level >= 2) return 'text-astro-text-secondary bg-white/5 border-white/10';
    return 'text-astro-danger bg-astro-danger/10 border-astro-danger/20';
  };
  return <div className="max-w-5xl mx-auto px-6 py-8" data-id="element-1149">
      {/* Page Header */}
      <div className="mb-8" data-id="element-1150">
        <button onClick={onBack} className="flex items-center gap-2 text-sm text-astro-text-secondary hover:text-white transition-colors mb-4" data-id="element-1151">
          <ArrowLeft size={16} data-id="element-1152" />
          Back to Dashboard
        </button>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-1153">
          <div className="flex items-center gap-4" data-id="element-1154">
            <h1 className="text-3xl font-bold text-white" data-id="element-1155">
              {environment.clientName}
            </h1>
            <span className="flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-bold uppercase tracking-wider border bg-white/5 text-astro-text-secondary border-white/10" data-id="element-1156">
              {getTypeIcon(environment.type)}
              {environment.type}
            </span>
            {environment.govCloud && <span className="flex items-center gap-1 px-2.5 py-1 rounded text-xs font-bold uppercase tracking-wider border bg-blue-500/10 text-blue-400 border-blue-500/25" data-id="element-1157">
                <Shield size={12} data-id="element-1158" />
                GOV
              </span>}
            {environment.cmmcLevel != null && <span className={`px-2.5 py-1 rounded text-xs font-bold uppercase tracking-wider border ${getCmmcColor(environment.cmmcLevel)}`} data-id="element-1159">
                CMMC L{environment.cmmcLevel}
              </span>}
          </div>
          <div className="flex items-center gap-2" data-id="element-1160">
            <span className="text-sm text-astro-text-secondary" data-id="element-1161">
              Compliance Score:
            </span>
            <span className={`text-xl font-mono font-bold ${environment.complianceScore > 85 ? 'text-astro-green' : environment.complianceScore > 70 ? 'text-white' : 'text-astro-danger'}`} data-id="element-1162">
              {environment.complianceScore}%
            </span>
          </div>
        </div>
      </div>

      {/* Remediation Assistant Header */}
      <div className="bg-[#0a0e14] border border-astro-green/20 rounded-lg p-6 mb-6" data-id="element-1163">
        <div className="flex items-center justify-between mb-4" data-id="element-1164">
          <div className="flex items-center gap-3" data-id="element-1165">
            <div className="w-10 h-10 rounded-lg bg-astro-green/15 flex items-center justify-center" data-id="element-1166">
              <Bot size={20} className="text-astro-green" data-id="element-1167" />
            </div>
            <div data-id="element-1168">
              <h2 className="text-lg font-bold text-white" data-id="element-1169">
                Remediation Assistant
              </h2>
              <p className="text-sm text-astro-text-secondary" data-id="element-1170">
                {environment.clientName} · {activeFindings.length} findings
              </p>
            </div>
          </div>
        </div>

        <div className="bg-astro-card border border-astro-border rounded-md p-4 mb-4" data-id="element-1171">
          <p className="text-sm text-astro-text-secondary leading-relaxed" data-id="element-1172">
            <span className="text-astro-green font-medium" data-id="element-1173">
              Analysis complete.
            </span>{' '}
            I found{' '}
            <span className="text-white font-bold" data-id="element-1174">
              {activeFindings.length} over-permissioned identities
            </span>{' '}
            in {environment.clientName}'s environment.{' '}
            {highCount > 0 && <>
                <span className="text-astro-danger font-bold" data-id="element-1175">
                  {highCount} high severity
                </span>{' '}
                findings require immediate action.{' '}
              </>}
            Below are my recommendations with suggested policy changes for each.
          </p>
        </div>

        <div className="flex items-center gap-2" data-id="element-1176">
          {highCount > 0 && <span className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-bold bg-astro-danger/10 text-astro-danger border border-astro-danger/20" data-id="element-1177">
              <span className="w-2 h-2 rounded-full bg-astro-danger" data-id="element-1178"></span>
              {highCount} High
            </span>}
          {mediumCount > 0 && <span className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-bold bg-white/5 text-astro-text-secondary border border-white/10" data-id="element-1179">
              <span className="w-2 h-2 rounded-full bg-astro-text-secondary" data-id="element-1180"></span>
              {mediumCount} Medium
            </span>}
          {lowCount > 0 && <span className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-bold bg-white/5 text-astro-text-secondary border border-white/10 opacity-80" data-id="element-1181">
              <span className="w-2 h-2 rounded-full bg-gray-500" data-id="element-1182"></span>
              {lowCount} Low
            </span>}
          <span className="text-xs text-astro-text-secondary ml-auto" data-id="element-1183">
            {dismissedFindings.length} resolved
          </span>
        </div>
      </div>

      {/* Findings List */}
      <div className="space-y-4" data-id="element-1184">
        {activeFindings.map(finding => {
        const isExpanded = expandedFinding === finding.id;
        return <div key={finding.id} className="bg-[#0a0e14] border border-astro-border rounded-lg overflow-hidden transition-all" data-id="element-1185">
              {/* Finding Header */}
              <button onClick={() => setExpandedFinding(isExpanded ? null : finding.id)} className="w-full p-4 flex items-center justify-between text-left hover:bg-white/[0.02] transition-colors" data-id="element-1186">
                <div className="flex items-center gap-4 min-w-0" data-id="element-1187">
                  <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider border shrink-0 ${finding.severity === 'HIGH' ? 'bg-astro-danger/10 text-astro-danger border-astro-danger/20' : finding.severity === 'MEDIUM' ? 'bg-white/5 text-astro-text-secondary border-white/10' : 'bg-[#1f2937] border-gray-700 text-gray-400'}`} data-id="element-1188">
                    {finding.severity}
                  </span>
                  <div className="min-w-0" data-id="element-1189">
                    <div className="flex items-center gap-2 mb-0.5" data-id="element-1190">
                      <span className="text-xs font-mono text-astro-green" data-id="element-1191">
                        {finding.id}
                      </span>
                      <span className="text-sm font-bold text-white truncate" data-id="element-1192">
                        {finding.title}
                      </span>
                    </div>
                    <span className="text-xs text-astro-text-secondary" data-id="element-1193">
                      {finding.resourceType} · {finding.resourceName}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0 ml-4" data-id="element-1194">
                  <span className="text-xs text-astro-text-secondary bg-white/5 px-2 py-1 rounded border border-white/10" data-id="element-1195">
                    {finding.affected} affected
                  </span>
                  {isExpanded ? <ChevronUp size={16} className="text-astro-text-secondary" data-id="element-1196" /> : <ChevronDown size={16} className="text-astro-text-secondary" data-id="element-1197" />}
                </div>
              </button>

              {/* Expanded Detail */}
              {isExpanded && <div className="px-4 pb-4 space-y-4 border-t border-astro-border pt-4" data-id="element-1198">
                  {/* Description */}
                  <div data-id="element-1199">
                    <span className="text-[10px] font-bold text-astro-text-secondary uppercase tracking-wider block mb-1.5" data-id="element-1200">
                      Description
                    </span>
                    <p className="text-sm text-astro-text-secondary/90 leading-relaxed" data-id="element-1201">
                      {finding.description}
                    </p>
                  </div>

                  {/* Affected Resource */}
                  <div data-id="element-1202">
                    <span className="text-[10px] font-bold text-astro-text-secondary uppercase tracking-wider block mb-1.5" data-id="element-1203">
                      Affected Resource
                    </span>
                    <div className="flex items-center gap-2 bg-astro-card border border-astro-border rounded px-3 py-2.5" data-id="element-1204">
                      <code className="text-xs font-mono text-astro-text-secondary flex-1 truncate" data-id="element-1205">
                        {finding.affectedResource}
                      </code>
                      <button onClick={e => {
                  e.stopPropagation();
                  handleCopyResource(finding.affectedResource, finding.id);
                }} className="text-astro-text-secondary hover:text-white transition-colors shrink-0" title="Copy" data-id="element-1206">
                        {copiedResource === finding.id ? <Check size={14} className="text-astro-green" data-id="element-1207" /> : <Copy size={14} data-id="element-1208" />}
                      </button>
                    </div>
                  </div>

                  {/* AI Recommendation */}
                  <div data-id="element-1209">
                    <div className="flex items-center gap-1.5 mb-1.5" data-id="element-1210">
                      <Sparkles size={12} className="text-astro-green" data-id="element-1211" />
                      <span className="text-[10px] font-bold text-astro-green uppercase tracking-wider" data-id="element-1212">
                        AI Recommendation
                      </span>
                    </div>
                    <p className="text-sm text-white/90 leading-relaxed" data-id="element-1213">
                      {finding.recommendation}
                    </p>
                  </div>

                  {/* Suggested Policy */}
                  {finding.suggestedPolicy && <div data-id="element-1214">
                      <div className="flex items-center gap-1.5 mb-1.5" data-id="element-1215">
                        <Shield size={12} className="text-astro-green" data-id="element-1216" />
                        <span className="text-[10px] font-bold text-astro-green uppercase tracking-wider" data-id="element-1217">
                          Suggested Policy
                        </span>
                      </div>
                      <pre className="text-xs font-mono text-astro-text-secondary bg-astro-card border border-astro-border rounded p-4 overflow-x-auto leading-relaxed" data-id="element-1218">
                        {finding.suggestedPolicy}
                      </pre>
                    </div>}

                  {/* Actions */}
                  <div className="flex flex-wrap items-center gap-3 pt-2" data-id="element-1219">
                    {createdTasks[finding.id] ? <div className="flex items-center gap-2 px-4 py-2 bg-astro-green/10 border border-astro-green/20 rounded text-xs font-bold text-astro-green" data-id="element-1220">
                        <CheckCircle2 size={14} data-id="element-1221" />
                        Task assigned to{' '}
                        {createdTasks[finding.id].assignee.name}
                      </div> : taskCreation?.findingId === finding.id /* Task Creation Workflow */ ? <div className="w-full bg-astro-card border border-astro-green/20 rounded-lg p-4 space-y-4" data-id="element-1222">
                        <div className="flex items-center justify-between" data-id="element-1223">
                          <div className="flex items-center gap-2" data-id="element-1224">
                            <ClipboardList size={14} className="text-astro-green" data-id="element-1225" />

                            <span className="text-sm font-bold text-white" data-id="element-1226">
                              Create Task
                            </span>
                          </div>
                          <button onClick={cancelTaskCreation} className="text-astro-text-secondary hover:text-white transition-colors p-1" data-id="element-1227">
                            <X size={14} data-id="element-1228" />
                          </button>
                        </div>

                        {/* Assignee Selector */}
                        <div data-id="element-1229">
                          <span className="text-[10px] text-astro-text-secondary uppercase tracking-wider block mb-1.5" data-id="element-1230">
                            <UserCircle size={10} className="inline mr-1" data-id="element-1231" />
                            Assign To
                          </span>
                          <div className="relative" data-id="element-1232">
                            <button onClick={() => setShowAssigneeDropdown(!showAssigneeDropdown)} className="w-full flex items-center justify-between px-3 py-2.5 bg-[#0a0e14] border border-astro-border rounded text-sm text-white hover:border-white/20 transition-colors" data-id="element-1233">
                              {taskCreation.assignee ? <span className="flex items-center gap-2.5" data-id="element-1234">
                                  <span className="w-7 h-7 rounded-full bg-astro-green/15 border border-astro-border flex items-center justify-center text-[10px] font-bold text-astro-green" data-id="element-1235">
                                    {taskCreation.assignee.initials}
                                  </span>
                                  <span data-id="element-1236">
                                    <span className="block text-sm text-white" data-id="element-1237">
                                      {taskCreation.assignee.name}
                                    </span>
                                    <span className="block text-[10px] text-astro-text-secondary" data-id="element-1238">
                                      {taskCreation.assignee.email}
                                    </span>
                                  </span>
                                </span> : <span className="text-astro-text-secondary/50" data-id="element-1239">
                                  Select a team member...
                                </span>}
                              <ChevronDown size={14} className={`text-astro-text-secondary transition-transform ${showAssigneeDropdown ? 'rotate-180' : ''}`} data-id="element-1240" />
                            </button>
                            {showAssigneeDropdown && <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-[#0a0e14] border border-astro-border rounded-lg shadow-2xl overflow-hidden max-h-[240px] overflow-y-auto" data-id="element-1241">
                                {teamMembers.map(member => <button key={member.id} onClick={() => {
                        setTaskCreation(prev => prev ? {
                          ...prev,
                          assignee: member
                        } : null);
                        setShowAssigneeDropdown(false);
                      }} className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${taskCreation.assignee?.id === member.id ? 'bg-astro-green/10' : 'hover:bg-white/[0.03]'}`} data-id="element-1242">
                                    <span className="w-7 h-7 rounded-full bg-astro-green/15 border border-astro-border flex items-center justify-center text-[10px] font-bold text-astro-green shrink-0" data-id="element-1243">
                                      {member.initials}
                                    </span>
                                    <div className="flex-1 min-w-0" data-id="element-1244">
                                      <span className="text-sm text-white block" data-id="element-1245">
                                        {member.name}
                                      </span>
                                      <span className="text-[10px] text-astro-text-secondary block" data-id="element-1246">
                                        {member.email}
                                      </span>
                                    </div>
                                    <span className={`flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider ${roleColors[member.role]}`} data-id="element-1247">
                                      {roleIcons[member.role]}
                                      {member.role}
                                    </span>
                                  </button>)}
                              </div>}
                          </div>
                        </div>

                        {/* Priority & Due Date Row */}
                        <div className="grid grid-cols-2 gap-3" data-id="element-1248">
                          <div data-id="element-1249">
                            <span className="text-[10px] text-astro-text-secondary uppercase tracking-wider block mb-1.5" data-id="element-1250">
                              <Flag size={10} className="inline mr-1" data-id="element-1251" />
                              Priority
                            </span>
                            <div className="flex gap-1.5" data-id="element-1252">
                              {(['Critical', 'High', 'Medium', 'Low'] as const).map(p => <button key={p} onClick={() => setTaskCreation(prev => prev ? {
                        ...prev,
                        priority: p
                      } : null)} className={`flex-1 py-1.5 text-[11px] font-bold rounded border transition-colors ${taskCreation.priority === p ? p === 'Critical' ? 'bg-astro-danger/10 border-astro-danger/30 text-astro-danger' : p === 'High' ? 'bg-orange-500/10 border-orange-500/30 text-orange-400' : p === 'Medium' ? 'bg-white/5 border-white/20 text-white' : 'bg-white/5 border-white/10 text-astro-text-secondary' : 'bg-[#0a0e14] border-astro-border text-astro-text-secondary/50 hover:text-astro-text-secondary hover:border-white/10'}`} data-id="element-1253">
                                  {p}
                                </button>)}
                            </div>
                          </div>
                          <div data-id="element-1254">
                            <span className="text-[10px] text-astro-text-secondary uppercase tracking-wider block mb-1.5" data-id="element-1255">
                              <Calendar size={10} className="inline mr-1" data-id="element-1256" />
                              Due Date
                            </span>
                            <input type="date" value={taskCreation.dueDate} onChange={e => setTaskCreation(prev => prev ? {
                      ...prev,
                      dueDate: e.target.value
                    } : null)} className="w-full px-3 py-1.5 bg-[#0a0e14] border border-astro-border rounded text-sm text-white focus:outline-none focus:border-astro-green/50 transition-colors [color-scheme:dark]" data-id="element-1257" />
                          </div>
                        </div>

                        {/* Notes */}
                        <div data-id="element-1258">
                          <span className="text-[10px] text-astro-text-secondary uppercase tracking-wider block mb-1.5" data-id="element-1259">
                            Notes (optional)
                          </span>
                          <textarea value={taskCreation.notes} onChange={e => setTaskCreation(prev => prev ? {
                    ...prev,
                    notes: e.target.value
                  } : null)} placeholder="Add context or instructions for the assignee..." rows={2} className="w-full px-3 py-2 bg-[#0a0e14] border border-astro-border rounded text-sm text-white placeholder:text-astro-text-secondary/30 focus:outline-none focus:border-astro-green/50 transition-colors resize-none" data-id="element-1260" />
                        </div>

                        {/* Submit */}
                        <div className="flex items-center justify-between pt-1" data-id="element-1261">
                          <button onClick={cancelTaskCreation} className="text-xs text-astro-text-secondary hover:text-white transition-colors" data-id="element-1262">
                            Cancel
                          </button>
                          <button onClick={handleCreateTask} disabled={!taskCreation.assignee} className="flex items-center gap-2 px-4 py-2 text-xs font-bold text-black bg-astro-green hover:bg-astro-green/90 rounded transition-colors shadow-[0_0_10px_rgba(34,197,94,0.15)] disabled:opacity-40 disabled:cursor-not-allowed" data-id="element-1263">
                            <Send size={12} data-id="element-1264" />
                            Assign & Create Task
                          </button>
                        </div>
                      </div> : <>
                        <button onClick={() => startTaskCreation(finding.id, finding.severity)} className="text-xs font-bold text-black bg-astro-green hover:bg-astro-green/90 px-4 py-2 rounded transition-colors flex items-center gap-2 shadow-[0_0_10px_rgba(34,197,94,0.15)]" data-id="element-1265">
                          <ClipboardList size={14} data-id="element-1266" />
                          Create Task
                        </button>
                        <button onClick={() => setDismissedFindings(prev => [...prev, finding.id])} className="text-xs font-medium text-white bg-white/5 border border-white/10 px-4 py-2 rounded hover:bg-white/10 transition-colors flex items-center gap-2" data-id="element-1267">
                          <BookmarkX size={14} data-id="element-1268" />
                          Mark as Accepted Risk
                        </button>
                        <button onClick={() => setDismissedFindings(prev => [...prev, finding.id])} className="text-xs font-medium text-astro-text-secondary border border-astro-border px-4 py-2 rounded hover:text-white hover:border-white/20 transition-colors flex items-center gap-2" data-id="element-1269">
                          <EyeOff size={14} data-id="element-1270" />
                          Suppress
                        </button>
                      </>}
                  </div>
                </div>}
            </div>;
      })}

        {activeFindings.length === 0 && <div className="bg-[#0a0e14] border border-astro-green/20 rounded-lg p-12 text-center" data-id="element-1271">
            <CheckCircle2 size={48} className="text-astro-green mx-auto mb-4" data-id="element-1272" />
            <h3 className="text-xl font-bold text-white mb-2" data-id="element-1273">
              All findings addressed
            </h3>
            <p className="text-astro-text-secondary max-w-md mx-auto" data-id="element-1274">
              Great work! You've triaged or resolved all detected
              vulnerabilities for {environment.clientName}.
            </p>
            <button onClick={onBack} className="mt-6 text-sm font-bold text-astro-green hover:text-astro-green-dark transition-colors" data-id="element-1275">
              Return to Dashboard
            </button>
          </div>}
      </div>
    </div>;
}